import { Injectable } from '@angular/core';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { Computed, StateRepository } from '@angular-ru/ngxs/decorators';
import { State } from '@ngxs/store';
import { Channel, ChannelService, Filter, FilterDataType, COMMON_FILTER_DATA_TYPES } from '@portals/api';

import { finalize, mergeMap, take, tap } from 'rxjs/operators';
import { Pagination } from '@portals/ui';
import { AuthState } from './auth.state';

interface StateModel {
	isLoading: boolean;
	data: Channel[];
	selected?: Channel;
	pagination: Pagination;
	searchFilter: object;
	filters: Filter[];
	filterParameter: object;
	defaultFilterParameter: object;
	filterDataTypes: FilterDataType[];
	totalRows: number;
	sort: string;
	populate: string[];
}

@StateRepository()
@State<StateModel>({
	name: 'channels',
	defaults: {
		isLoading: false,
		totalRows: 0,
		data: [],
		pagination: {
			'pagination[start]': 0,
			'pagination[limit]': 25,
		},
		sort: 'updatedAt:desc',
		populate: ['deep'],
		searchFilter: {},
		filters: JSON.parse(sessionStorage.getItem('channels.filters') || '[]'),
		filterParameter: JSON.parse(sessionStorage.getItem('channels.filterParameter') || '{}'),
		defaultFilterParameter: {},
		filterDataTypes: [
			{
				key: 'is_active',
				text: 'ACTIVE',
				field: 'select',
				condition: 'boolean',
				options: [
					{
						label: 'False',
						value: false,
					},
					{
						label: 'True',
						value: true,
					},
				],
			},
			{
				key: 'name',
				text: 'Name',
				field: 'text',
				condition: 'text',
			},
			COMMON_FILTER_DATA_TYPES['createdAt'],
		],
	},
})
@Injectable()
export class ChannelsState extends NgxsDataRepository<StateModel> {
	@Computed()
	get isLoading() {
		return this.snapshot.isLoading;
	}

	@Computed()
	get hasData() {
		return !!this.snapshot.data;
	}

	@Computed()
	get data() {
		return this.snapshot.data;
	}

	@Computed()
	get item() {
		return this.snapshot.selected;
	}

	@Computed()
	get pagination() {
		return this.snapshot.pagination;
	}

	@Computed()
	get searchFilter() {
		return this.snapshot.searchFilter;
	}

	@Computed()
	get filterParameter() {
		return this.snapshot.filterParameter;
	}

	@Computed()
	get defaultFilterParameter() {
		return this.snapshot.defaultFilterParameter;
	}

	@Computed()
	get totalRows() {
		return this.snapshot.totalRows;
	}

	@Computed()
	get sort() {
		return this.snapshot.sort;
	}

	@Computed()
	get populate() {
		return this.snapshot.populate;
	}

	constructor(private channelService: ChannelService, private user: AuthState) {
		super();
	}

	clear() {
		this.patchState({
			sort: 'updatedAt:desc',
			pagination: {
				'pagination[start]': 0,
				'pagination[limit]': 25,
			},
		});
	}

	load(initialLoad = false) {
		this.patchState({ isLoading: true });

		if (initialLoad) {
			this.clear();
		}
		return this.channelService.find({ params: { ...this.getCombinedParams() } }).pipe(
			tap(({ data, total }) => {
				this.patchState({
					data,
					totalRows: total,
				});
			}),
			finalize(() => {
				this.patchState({ isLoading: false });
			})
		);
	}
	topFive() {
		return this.channelService.getTopFive();
	}
	findOne(id: number | string) {
		return this.channelService.findOne(id);
	}

	findOnebyUUID(uuid: string) {
		console.log(uuid)
		return this.channelService.findOneByUUID(uuid);
	}

	findChannelByHash(channelHash: string) {
		return this.channelService.findByHash(channelHash);
	}

	create(payload: FormData) {
		this.patchState({ isLoading: true });

		return this.channelService.create(payload);
	}

	update(id: number, formData: FormData) {
		this.patchState({ isLoading: true });

		return this.channelService.update(id, formData).pipe(mergeMap(() => this.load()));
	}

	setPagination(pagination: Pagination) {
		this.patchState({ pagination });
		this.load().subscribe();
	}

	setSort(key: string, order: string) {
		const parsedOrder = `${!order ? 'desc' : order}`;
		const sort = `${key}:${parsedOrder}`;
		this.patchState({ sort });
		this.load().subscribe();
	}

	searchFields(keys: string[], value?: string): void {
		const searchFilter: any = {};

		if (value) {
			keys.forEach((key, index) => {
				searchFilter[`_where[_or][${index}][${key}_contains]`] = value;
			});
		}

		this.patchState({ searchFilter });
		this.load().subscribe();
	}

	setFilterParameter(filterParameter: object): void {
		this.patchState({ filterParameter });
		this.load().subscribe();
	}

	setFilters(filters: Filter[]): void {
		this.patchState({ filters });
	}

	setDefaultFilterParameter(defaultFilterParameter: object): void {
		this.patchState({ defaultFilterParameter });
	}

	setPopulate(populate: string[]): void {
		this.patchState({ populate });
	}

	getCombinedParams(): object {
		let filterParameter: any = { ...this.filterParameter };
		// combine defaultFilterParameter to filterParameter
		filterParameter = {
			filters: {
				...this.defaultFilterParameter,
				...(filterParameter?.filters || {}),
			},
		};
		// filter by project handling
		if (this.user?.data?.project?.id) {
			filterParameter = {
				filters: {
					...(filterParameter?.filters || {}),
					project: this.user.data.project.id,
				},
			};
		}

		return {
			sort: this.sort,
			populate: this.populate,
			...this.pagination,
			...this.searchFilter,
			...filterParameter,
		};
	}

	getAllData() {
		const params = {
			...this.getCombinedParams(),
			'pagination[limit]': -1,
		};
		return this.channelService.find({ params });
	}
}
